import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const DigitalAssetsPage = () => {
  const klCarpoolFeatures = [
    {
      icon: 'ri-map-pin-time-line',
      title: 'Real-Time Matching',
      description: 'Advanced algorithms connect riders and drivers instantly based on routes and schedules.',
    },
    {
      icon: 'ri-building-line',
      title: 'Corporate Programs',
      description: 'Customized carpool solutions for businesses to reduce employee commute costs.',
    },
    {
      icon: 'ri-leaf-line',
      title: 'ESG Reporting',
      description: 'Track and report carbon reduction metrics for sustainability initiatives.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Safety First',
      description: 'Verified users, in-app safety features, and 24/7 support for peace of mind.',
    },
  ];

  const marketplaceFeatures = [
    {
      icon: 'ri-line-chart-line',
      title: 'Live Pricing',
      description: 'Real-time market pricing with transparent rate updates and historical data.',
    },
    {
      icon: 'ri-database-line',
      title: 'Transparent Inventory',
      description: 'Complete visibility into available stock, quality grades, and delivery schedules.',
    },
    {
      icon: 'ri-bank-line',
      title: 'Integrated Financing',
      description: 'Seamless access to Exim Bank financing directly through the platform.',
    },
    {
      icon: 'ri-global-line',
      title: 'Global Network',
      description: 'Connect with mills and buyers across five continents in one marketplace.',
    },
  ];

  const smeBrandingFeatures = [
    {
      icon: 'ri-store-3-line',
      title: 'Multi-Channel Selling',
      description: 'Sell across TikTok Shop, Shopee, Lazada, and your own e-commerce store from one dashboard.',
    },
    {
      icon: 'ri-robot-line',
      title: 'Physical AI Integration',
      description: 'Physical AI-powered product management, logistics automation, and intelligent inventory control across all channels.',
    },
    {
      icon: 'ri-bar-chart-grouped-line',
      title: 'Performance Analytics',
      description: 'Unified analytics across all channels to track sales, ROI, and customer engagement.',
    },
    {
      icon: 'ri-palette-line',
      title: 'Brand Identity Builder',
      description: 'Professional branding tools including logo, packaging design, and brand guidelines for SMEs.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="relative pt-0 pb-0">
        <div className="relative w-full min-h-[320px] md:min-h-[380px]">
          <img
            src="https://public.readdy.ai/ai/img_res/e32376ce4bc908f0eabac6cfc51352e7.jpg"
            alt="Strategic Digital Assets"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
          <div className="relative z-10 flex items-center justify-center min-h-[320px] md:min-h-[380px] py-20 px-4 sm:px-6">
            <div className="text-center w-full max-w-4xl mx-auto">
              <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-3 sm:mb-4 leading-tight">
                Strategic Digital Assets
              </h1>
              <p className="text-sm sm:text-lg md:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed px-2">
                Flagship digital platforms showcasing our <strong>tech-forward transformation</strong> and commitment to innovation
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Platform 01: KL Carpool */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24">
            <div className="w-full h-96">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20smartphone%20displaying%20clean%20minimalist%20carpool%20ride%20sharing%20app%20interface%20with%20map%20and%20matching%20features%20held%20by%20Malaysian%20Malay%20hand%20against%20blurred%20Kuala%20Lumpur%20city%20background%20showing%20sustainable%20urban%20transportation%20technology%20bright%20daylight%20professional%20product%20photography%20style%20with%20green%20eco%20friendly%20theme&width=800&height=600&seq=klcarpool-detail-my-v2&orientation=landscape"
                alt="KL Carpool Platform"
                className="w-full h-full object-cover object-top rounded-2xl shadow-xl"
              />
            </div>

            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-lg">
                  <i className="ri-car-line text-2xl text-primary-600"></i>
                </div>
                <span className="text-sm font-semibold text-primary-600 uppercase tracking-wide">
                  Digital Platform 01
                </span>
              </div>

              <h2 className="text-4xl font-display font-bold text-gray-900 mb-4">
                KL Carpool
              </h2>

              <p className="text-xl font-semibold text-primary-600 mb-6">
                Sustainable Mobility for Modern Cities
              </p>

              <p className="text-gray-600 mb-8 leading-relaxed text-lg">
                <strong>KL Carpool</strong> is our signature sustainable-mobility venture, offering real-time ride-matching, corporate carpool programs, and an ESG-focused narrative that helps businesses and individuals reduce their carbon footprint.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                {klCarpoolFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-50 rounded-lg flex-shrink-0">
                      <i className={`${feature.icon} text-xl text-primary-600`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <a
                  href="https://www.klcarpool.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  <span>Visit Platform</span>
                  <i className="ri-external-link-line"></i>
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span>Contact Us</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>

          {/* Platform 02: B2B Marketplace */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24">
            <div className="lg:order-2 w-full h-96">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20business%20laptop%20displaying%20modern%20B2B%20e-commerce%20marketplace%20dashboard%20with%20clean%20interface%20showing%20product%20listings%20pricing%20charts%20and%20inventory%20data%20on%20desk%20in%20bright%20Malaysian%20office%20environment%20contemporary%20business%20technology%20photography%20style%20with%20palm%20oil%20trading%20theme&width=800&height=600&seq=marketplace-detail-my-v2&orientation=landscape"
                alt="Drixem Palm Oil B2B E-Marketplace"
                className="w-full h-full object-cover object-top rounded-2xl shadow-xl"
              />
            </div>

            <div className="lg:order-1">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-accent-100 rounded-lg">
                  <i className="ri-shopping-cart-line text-2xl text-accent-600"></i>
                </div>
                <span className="text-sm font-semibold text-accent-600 uppercase tracking-wide">
                  Digital Platform 02
                </span>
              </div>

              <h2 className="text-4xl font-display font-bold text-gray-900 mb-4">
                Drixem Palm Oil B2B E-Marketplace
              </h2>

              <p className="text-xl font-semibold text-accent-600 mb-6">
                Next-Gen Trading Hub
              </p>

              <p className="text-gray-600 mb-8 leading-relaxed text-lg">
                A revolutionary <strong>B2B trading platform</strong> that links mills directly to global buyers, providing live pricing, transparent inventory management, and integrated financing via Exim Bank for seamless transactions.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                {marketplaceFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-accent-50 rounded-lg flex-shrink-0">
                      <i className={`${feature.icon} text-xl text-accent-600`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <a
                  href="https://www.drixems.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-accent-600 hover:bg-accent-700 text-white font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  <span>Visit Platform</span>
                  <i className="ri-external-link-line"></i>
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 text-accent-600 hover:text-accent-700 font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span>Contact Us</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>

          {/* Platform 03: AI Branding & Marketing for SME */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="w-full h-96">
              <img
                src="https://readdy.ai/api/search-image?query=Close%20up%20screenshot%20of%20TikTok%20Shop%20mobile%20app%20interface%20showing%20product%20listings%20with%20buy%20now%20buttons%20live%20shopping%20feature%20product%20cards%20with%20prices%20and%20shopping%20cart%20on%20smartphone%20screen%20bright%20colorful%20social%20commerce%20interface%20with%20orange%20and%20red%20TikTok%20branding%20elements%20professional%20app%20screenshot%20photography%20style&width=800&height=600&seq=tiktok-shop-actual-v2&orientation=landscape"
                alt="AI Branding & Marketing for SME - TikTok Shop"
                className="w-full h-full object-cover object-top rounded-2xl shadow-xl"
              />
            </div>

            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-lg">
                  <i className="ri-store-3-line text-2xl text-primary-600"></i>
                </div>
                <span className="text-sm font-semibold text-primary-600 uppercase tracking-wide">
                  Digital Platform 03
                </span>
              </div>

              <h2 className="text-4xl font-display font-bold text-gray-900 mb-4">
                AI Branding &amp; Marketing for SME
              </h2>

              <p className="text-xl font-semibold text-primary-600 mb-6">
                Maximize Sales Across Every E-Commerce Channel
              </p>

              <p className="text-gray-600 mb-8 leading-relaxed text-lg">
                An <strong>AI-powered branding and marketing platform</strong> built for SME companies, leveraging <strong>TikTok Shop, Shopee, Lazada</strong>, and other e-commerce channels to drive maximum sales, brand visibility, and customer engagement across Southeast Asia.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                {smeBrandingFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-primary-50 rounded-lg flex-shrink-0">
                      <i className={`${feature.icon} text-xl text-primary-600`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  <span>Inquire Now</span>
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              Why Invest in Our Digital Assets?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Scalable platforms with proven technology and recurring revenue models
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center hover:shadow-xl transition-all">
              <div className="w-16 h-16 flex items-center justify-center bg-primary-100 rounded-full mx-auto mb-6">
                <i className="ri-rocket-line text-3xl text-primary-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Scalable Technology
              </h3>
              <p className="text-gray-600">
                Built on modern cloud infrastructure with the ability to scale rapidly across markets and user bases.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center hover:shadow-xl transition-all">
              <div className="w-16 h-16 flex items-center justify-center bg-primary-100 rounded-full mx-auto mb-6">
                <i className="ri-repeat-line text-3xl text-primary-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Recurring Revenue
              </h3>
              <p className="text-gray-600">
                Subscription-based and transaction-fee models ensure predictable, long-term revenue streams.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center hover:shadow-xl transition-all">
              <div className="w-16 h-16 flex items-center justify-center bg-primary-100 rounded-full mx-auto mb-6">
                <i className="ri-award-line text-3xl text-primary-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                MSC-Status Backing
              </h3>
              <p className="text-gray-600">
                Developed by Drixem Sdn Bhd with MSC status, ensuring quality and government recognition.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DigitalAssetsPage;
